import { useCallback, useEffect, useMemo, useState } from 'react';
import { Upload, Button, Col } from 'antd';
import { UploadOutlined, SyncOutlined } from '@ant-design/icons';
import { FileText, X } from 'react-feather';
import { Loading } from 'Components';
import './index.scss';

const UploadingBox = () => (
  <div>
    <SyncOutlined spin />
    <p>Uploading file...</p>
  </div>
);

const FileBadge = ({ name, onClear }) => (
  <div style={{ display: 'flex' }}>
    <Col
      flex="auto"
      className="uploaded-file"
    >
      <FileText
        size="48"
        strokeWidth="0.5px"
      />
      <p>{name}</p>
    </Col>
    <Col flex="10px">
      <X
        style={{ cursor: 'pointer' }}
        onClick={onClear}
      />
    </Col>
  </div>
);

const DraggerInner = ({ Icon, text }) => (
  <>
    <p className="ant-upload-drag-icon">
      <Icon />
    </p>
    <p className="ant-upload-text">{text}</p>
  </>
);

function UploadFileInput({
  form,
  field,
  isCompact = false,
  disabled = false,
  onFileSelect = () => {},
}) {
  const [uploadedName, setUploadedName] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const files = await Weaver.File.list();
        if (mounted) setExistingFiles(files || []);
      } catch {
        if (mounted) setExistingFiles([]);
      } finally {
        if (mounted) setFetching(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const val = form?.getFieldValue(field);
    if (!val) {
      setUploadedName(null);
      return;
    }

    if (typeof val === 'object') {
      const maybeName = val.fileName || val.name || val.filename || null;
      if (maybeName) {
        setUploadedName(maybeName);
        return;
      }

      if (typeof val.id === 'function') {
        const id = val.id();
        const match = existingFiles.find(f => f.id() === id);
        setUploadedName(match?.fileName ?? null);
        return;
      }
    }

    if (typeof val === 'string') {
      const match = existingFiles.find(f => f.id() === val);
      setUploadedName(match?.fileName ?? val);
    }
  }, [form, field, existingFiles]);

  const clearSelection = useCallback(() => {
    form?.setFieldsValue({ [field]: undefined });
    setUploadedName(null);
  }, [form, field]);

  const handleCustomUpload = useCallback(async ({ file, onSuccess, onError }) => {
    if (!file) return;
    setUploading(true);
    try {
      file.uploadTime = new Date();

      const weaverFile = new Weaver.File(file);
      form?.setFieldsValue({ [field]: weaverFile.id?.() ? weaverFile.id() : weaverFile });

      setUploadedName(weaverFile.fileName);
      onFileSelect({ [field]: weaverFile.id?.() ? weaverFile.id() : weaverFile });

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      if (onError) onError(err);
    } finally {
      setUploading(false);
    }
  }, [field, form, onFileSelect]);

  const uploadProps = useMemo(() => ({
    name: 'file',
    multiple: false,
    showUploadList: false,
    disabled: uploading || disabled,
    customRequest: handleCustomUpload,
  }), [disabled, uploading, handleCustomUpload]);

  if (fetching) {
    return (
      <Loading
        style={{
          display: 'flex',
          textAlign: 'center',
          width: '100%',
          height: 200,
          alignItems: 'center',
          background: 'var(--background)',
        }}
      />
    );
  }

  if (!isCompact) {
    return (
      <div className="uploader uploader--extended">
        {uploading ? (
          <UploadingBox />
        ) : uploadedName ? (
          <FileBadge name={uploadedName} onClear={clearSelection} />
        ) : (
          <Upload.Dragger className="uploader__dragger" {...uploadProps}>
            <DraggerInner Icon={UploadOutlined} text="Click or drag file to this area to upload" />
          </Upload.Dragger>
        )}
      </div>
    );
  }

  return (
    <div className="uploader uploader--compact">
      {uploading ? (
        <UploadingBox />
      ) : uploadedName ? (
        <FileBadge compact name={uploadedName} onClear={clearSelection} />
      ) : (
        <Upload {...uploadProps} className="uploader__compact-upload">
          <Button
            block
            icon={<UploadOutlined />}
            disabled={uploading || disabled}
          >
            Click to upload file
          </Button>
        </Upload>
      )}
    </div>
  );
}

export default UploadFileInput;
